import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { AlertTriangle, Clock, CheckCircle, ChevronRight, Upload } from 'lucide-react';
interface VentureWorkspaceProps {
  userRole: 'Program Manager' | 'Founder';
}
const VentureWorkspace: React.FC<VentureWorkspaceProps> = ({
  userRole
}) => {
  const {
    ventureId
  } = useParams<{
    ventureId: string;
  }>();
  const [activeTab, setActiveTab] = useState('overview');
  const [showInsights, setShowInsights] = useState(true);
  return <div className="w-full p-6" data-id="element-1336">
      <div className="flex justify-between items-center mb-6" data-id="element-1337">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-1338">CardioSense</h1>
        <div className="flex items-center" data-id="element-1339">
          <div className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mr-3" data-id="element-1340">
            Discovery
          </div>
          <div className="text-xs text-gray-600" data-id="element-1341">Credits: 180 / 300</div>
        </div>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="element-1342">
        <TabsList className="mb-6" data-id="element-1343">
          <TabsTrigger value="overview" data-id="element-1344">Overview</TabsTrigger>
          <TabsTrigger value="context" data-id="element-1345">Context</TabsTrigger>
          <TabsTrigger value="insights" data-id="element-1346">Insights</TabsTrigger>
          <TabsTrigger value="people" data-id="element-1347">People</TabsTrigger>
          <TabsTrigger value="assets" data-id="element-1348">Assets</TabsTrigger>
          <TabsTrigger value="calendar" data-id="element-1349">Calendar</TabsTrigger>
          <TabsTrigger value="competitors" data-id="element-1350">Competitors</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-1351">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1352">
            <div data-id="element-1353">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1354">
                Problem
              </h3>
              <p className="text-gray-800" data-id="element-1355">
                Post-discharge cardiac rehab adherence drops in weeks 2–3,
                hurting outcomes and reimbursement.
              </p>
            </div>
            <div data-id="element-1356">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1357">
                Value Proposition Hypothesis
              </h3>
              <p className="text-gray-800" data-id="element-1358">
                AI chatbot to send reminders to patient cell phones
              </p>
            </div>
            <div data-id="element-1359">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1360">ICP</h3>
              <p className="text-gray-800" data-id="element-1361">
                Cardiac rehab program directors at mid-to-large health systems.
              </p>
            </div>
            <div data-id="element-1362">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1363">Stage</h3>
              <div className="flex items-center" data-id="element-1364">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full" data-id="element-1365">
                  Discovery
                </span>
              </div>
            </div>
            <div data-id="element-1366">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1367">
                Goals (this week)
              </h3>
              <p className="text-gray-800" data-id="element-1368">
                Collect 3 interviews; publish 3 validated insights.
              </p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-1369">
            <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-1370">Team</h3>
            <div className="flex space-x-3" data-id="element-1371">
              <div className="flex items-center space-x-2" data-id="element-1372">
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white" data-id="element-1373">
                  JC
                </div>
                <span className="text-sm text-gray-800" data-id="element-1374">Jamie Cole</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-1375">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white" data-id="element-1376">
                  PS
                </div>
                <span className="text-sm text-gray-800" data-id="element-1377">Priya Shah</span>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="context" className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-1378">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-6 flex items-start" data-id="element-1379">
            <AlertTriangle size={16} className="text-yellow-600 mr-2 mt-0.5" data-id="element-1380" />
            <div data-id="element-1381">
              <p className="text-sm text-yellow-800 font-medium" data-id="element-1382">
                Innovation-only Data
              </p>
              <p className="text-xs text-yellow-700" data-id="element-1383">Do not include PHI/PII.</p>
            </div>
          </div>
          <div className="border border-dashed border-gray-300 rounded-lg p-6 mb-6 text-center" data-id="element-1384">
            <Upload size={24} className="mx-auto mb-2 text-gray-400" data-id="element-1385" />
            <h3 className="text-gray-600 font-medium mb-1" data-id="element-1386">Add artifact</h3>
            <p className="text-xs text-gray-500" data-id="element-1387">
              Drag and drop or click to upload
            </p>
            <button className="mt-3 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors" data-id="element-1388">
              Upload file
            </button>
          </div>
          <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1389">
            Latest Artifacts
          </h3>
          <div className="p-4 border border-gray-200 rounded-md" data-id="element-1390">
            <div className="flex items-center" data-id="element-1391">
              <div size={16} className="text-gray-500 mr-2" data-id="element-1392" />
              <h4 className="text-sm font-medium text-gray-800" data-id="element-1393">
                CRN-001 Nurse Manager Interview
              </h4>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6" data-id="element-1394">2025-09-22</p>
            <div className="flex mt-2 ml-6" data-id="element-1395">
              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full mr-1" data-id="element-1396">
                Stakeholder: Nurse Manager
              </span>
              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full mr-1" data-id="element-1397">
                Clinical: Cardiology
              </span>
              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full" data-id="element-1398">
                Sensitivity: Private
              </span>
            </div>
            <div className="mt-3 ml-6" data-id="element-1399">
              <button className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors" data-id="element-1400">
                View details
              </button>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="insights" className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-1401">
          {!showInsights ? <div className="text-center py-10" data-id="element-1402">
              <h3 className="text-gray-600 font-medium mb-2" data-id="element-1403">
                No published insights yet
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1404">
                Run an agent or add insights manually.
              </p>
              <div className="flex justify-center space-x-3" data-id="element-1405">
                <button className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors" data-id="element-1406">
                  Run an agent
                </button>
                <button className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors" data-id="element-1407">
                  Add manually
                </button>
              </div>
            </div> : <div data-id="element-1408">
              <div className="mb-4" data-id="element-1409">
                <div className="p-4 border border-gray-200 rounded-md mb-3" data-id="element-1410">
                  <div className="flex justify-between mb-1" data-id="element-1411">
                    <h4 className="font-medium text-gray-800" data-id="element-1412">
                      Transportation barriers drive week 2–3 no-shows; ride
                      prompts could trigger early intervention.
                    </h4>
                    <span className="text-xs py-0.5 px-2 bg-green-100 text-green-800 rounded-full" data-id="element-1413">
                      High Confidence
                    </span>
                  </div>
                  <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 mb-2" data-id="element-1414">
                    <span className="font-medium" data-id="element-1415">Citation:</span>{' '}
                    "Transportation is number one… Weeks two and three are where
                    no-shows spike."
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1416">
                    <div className="flex space-x-2" data-id="element-1417">
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1418">
                        Edit
                      </button>
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1419">
                        Contribute anonymized
                      </button>
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1420">
                        View source
                      </button>
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full" data-id="element-1421">
                      Private
                    </span>
                  </div>
                </div>
                <div className="p-4 border border-gray-200 rounded-md mb-3" data-id="element-1422">
                  <div className="flex justify-between mb-1" data-id="element-1423">
                    <h4 className="font-medium text-gray-800" data-id="element-1424">
                      Human phone calls the day before and morning-of reduce
                      no-shows by ~15 points compared to automated texts.
                    </h4>
                    <span className="text-xs py-0.5 px-2 bg-green-100 text-green-800 rounded-full" data-id="element-1425">
                      High Confidence
                    </span>
                  </div>
                  <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 mb-2" data-id="element-1426">
                    <span className="font-medium" data-id="element-1427">Citation:</span> "If we call…
                    we cut no-shows by around 15 percentage points."
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1428">
                    <div className="flex space-x-2" data-id="element-1429">
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1430">
                        Edit
                      </button>
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1431">
                        Contribute anonymized
                      </button>
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1432">
                        View source
                      </button>
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full" data-id="element-1433">
                      Private
                    </span>
                  </div>
                </div>
                <div className="p-4 border border-gray-200 rounded-md" data-id="element-1434">
                  <div className="flex justify-between mb-1" data-id="element-1435">
                    <h4 className="font-medium text-gray-800" data-id="element-1436">
                      Patients misinterpret session cadence as optional;
                      reinforce insurance-authorized plan at discharge and week
                      one.
                    </h4>
                    <span className="text-xs py-0.5 px-2 bg-yellow-100 text-yellow-800 rounded-full" data-id="element-1437">
                      Medium Confidence
                    </span>
                  </div>
                  <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 mb-2" data-id="element-1438">
                    <span className="font-medium" data-id="element-1439">Citation:</span> "Patients
                    hear 'as needed'… Some think the first session was a
                    check-in and the rest are optional."
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1440">
                    <div className="flex space-x-2" data-id="element-1441">
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1442">
                        Edit
                      </button>
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1443">
                        Contribute anonymized
                      </button>
                      <button className="text-xs px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors" data-id="element-1444">
                        View source
                      </button>
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full" data-id="element-1445">
                      Anonymized
                    </span>
                  </div>
                </div>
              </div>
              <button className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors" data-id="element-1446">
                Add new insight
              </button>
            </div>}
        </TabsContent>
      </Tabs>
    </div>;
};
export default VentureWorkspace;